import React, { useEffect, useRef, useState } from "react";
import { LogoMark } from "./Logo";

/**
 * One-second entrance animation played the first time a dealer lands on the
 * dashboard. Uses localStorage['tm_splash_seen'] as the guard. Written to be
 * safe under React 18 StrictMode double-invoke: the localStorage flag is
 * flipped only when the timer completes, not on mount.
 */
export default function OnboardingSplash({ onDone }) {
  const [visible, setVisible] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("tm_splash_seen") !== "1";
  });
  const [fading, setFading] = useState(false);
  const timerRef = useRef(null);
  const doneRef = useRef(false);

  useEffect(() => {
    if (!visible) { onDone?.(); return; }
    timerRef.current = setTimeout(() => {
      if (doneRef.current) return;
      doneRef.current = true;
      try { localStorage.setItem("tm_splash_seen", "1"); } catch (err) { console.debug("splash: storage unavailable", err); }
      setFading(true);
      // Allow the fade animation to play, then unmount cleanly.
      setTimeout(() => { setVisible(false); onDone?.(); }, 350);
    }, 900);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    // Only ever run once per component instance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!visible) return null;
  return (
    <div
      className={`fixed inset-0 z-[60] flex items-center justify-center bg-white transition-opacity duration-300 ${fading ? "opacity-0 pointer-events-none" : "opacity-100"}`}
      data-testid="onboarding-splash"
      aria-hidden={fading}
    >
      <div className="tm-splash-scale flex flex-col items-center gap-4">
        <LogoMark size={96} />
        <div className="text-sm font-semibold tracking-tight text-[#0F1115]">Welcome to TreadMargin</div>
        <div className="text-xs text-[#3B424B]">Preparing your dashboard…</div>
      </div>
      <style>{`
        .tm-splash-scale { animation: tmSplashScale 0.7s cubic-bezier(0.22,1,0.36,1) both; }
        @keyframes tmSplashScale {
          0% { transform: translateY(6px) scale(0.94); opacity: 0; }
          60% { transform: translateY(0) scale(1); opacity: 1; }
          100% { transform: translateY(-4px) scale(1.02); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
